import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import {
  DEFAULT_PAGE_LIMIT,
  OffsetPaginationBar,
} from "@/shared/ui/OffsetPaginationBar";
import { QueryState } from "@/shared/ui/QueryState";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  cn,
  toast,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import { ArrowLeftIcon, HistoryIcon, InboxIcon, PlayIcon } from "lucide-react";
import { useState } from "react";
import {
  useNotificationLogsQuery,
  useNotificationQueueQuery,
  useProcessNotificationQueue,
  useWhatsAppWorkerStatusQuery,
} from "../api/queries";
import { NotificationHistorySummaryCards } from "../ui/NotificationHistorySummaryCards";
import { NotificationLogsTable } from "../ui/NotificationLogsTable";
import { NotificationQueueTable } from "../ui/NotificationQueueTable";
import { NotificationRecordsFilterBar } from "../ui/NotificationRecordsFilterBar";
import { WorkerStatusBanner } from "../ui/WorkerStatusBanner";

type HistoryTab = "logs" | "queue";

export function PackageNotificationsLogsPage() {
  const nav = useNavigate();
  const workerQuery = useWhatsAppWorkerStatusQuery();
  const processQueue = useProcessNotificationQueue();

  const [tab, setTab] = useState<HistoryTab>("logs");
  const [logsStatus, setLogsStatus] = useState("all");
  const [queueStatus, setQueueStatus] = useState("all");
  const [logsOffset, setLogsOffset] = useState(0);
  const [queueOffset, setQueueOffset] = useState(0);
  const limit = DEFAULT_PAGE_LIMIT;

  const logsTotalQuery = useNotificationLogsQuery({ limit: 1, offset: 0 });
  const logsQuery = useNotificationLogsQuery({
    limit,
    offset: logsOffset,
    status: logsStatus !== "all" ? logsStatus : undefined,
  });
  const queueQuery = useNotificationQueueQuery({
    limit,
    offset: queueOffset,
    status: queueStatus !== "all" ? queueStatus : undefined,
  });

  const logsTotal = logsTotalQuery.data?.meta.total ?? 0;
  const queueStats = queueQuery.data?.stats ?? {
    pending: 0,
    failed: 0,
    total: 0,
  };
  const isSummaryLoading = logsTotalQuery.isLoading || queueQuery.isLoading;

  const handleLogsStatusChange = (status: string) => {
    setLogsStatus(status);
    setLogsOffset(0);
  };

  const handleQueueStatusChange = (status: string) => {
    setQueueStatus(status);
    setQueueOffset(0);
  };

  const handleQueueFilterFromCard = (status: "pending" | "failed" | "all") => {
    setTab("queue");
    setQueueStatus(status);
    setQueueOffset(0);
  };

  const handleProcessPendingQueue = async () => {
    const pending = queueStats.pending;
    if (pending === 0) {
      toast.info("ບໍ່ມີລາຍການລໍຖ້າໃນຄິວ");
      return;
    }

    try {
      let sent = 0;
      let failed = 0;
      let paused = false;
      for (let i = 0; i < pending; i += 1) {
        const result = await processQueue.mutateAsync({ batchSize: 1 });
        sent += result.sent;
        failed += result.failed;
        if (result.paused) {
          paused = true;
          break;
        }
      }

      if (paused) {
        toast.warning(`ສົ່ງ ${sent} ລາຍການ — Worker ຢຸດຊົ່ວຄາວ`);
      } else {
        toast.success(`ປະມວນຜົນຄິວ: sent ${sent}, failed ${failed}`);
      }
    } catch {
      toast.error("ປະມວນຜົນຄິວບໍ່ສຳເລັດ");
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            {
              label: "ແຈ້ງເຕືອນແພັກ",
              to: "/app/package-notifications",
            },
            { label: "ປະຫວັດ" },
          ]}
        />
      </Header>
      <Main>
        <div className="mx-auto w-full max-w-7xl space-y-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="flex items-center gap-2 font-bold text-2xl tracking-tight">
                  <HistoryIcon className="size-6" />
                  ປະຫວັດ ແລະ ຄິວ
                </h2>
                <WorkerStatusBanner health={workerQuery.data} />
              </div>
              <p className="text-muted-foreground">
                ບັນທຶກການສົ່ງ WhatsApp ແລະ ລາຍການ enqueue ໃນຄິວ
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/package-notifications" })}
            >
              <ArrowLeftIcon className="mr-1 size-4" />
              ກັບຄືນ
            </Button>
          </div>

          <NotificationHistorySummaryCards
            logsTotal={logsTotal}
            queueStats={queueStats}
            isLoading={isSummaryLoading}
            activeTab={tab}
            onTabChange={setTab}
            onQueueStatusFilter={handleQueueFilterFromCard}
          />

          <div className="flex flex-wrap gap-2 border-b pb-1">
            <Button
              type="button"
              size="sm"
              variant={tab === "logs" ? "default" : "ghost"}
              className={cn(
                "h-9 gap-2",
                tab === "logs" && "shadow-sm",
              )}
              onClick={() => setTab("logs")}
            >
              <HistoryIcon className="size-4" />
              ປະຫວັດການສົ່ງ
              <Badge
                variant={tab === "logs" ? "secondary" : "outline"}
                className="h-5 min-w-5 px-1.5 text-[10px] tabular-nums"
              >
                {logsTotal.toLocaleString("lo-LA")}
              </Badge>
            </Button>
            <Button
              type="button"
              size="sm"
              variant={tab === "queue" ? "default" : "ghost"}
              className={cn(
                "h-9 gap-2",
                tab === "queue" && "shadow-sm",
              )}
              onClick={() => setTab("queue")}
            >
              <InboxIcon className="size-4" />
              ຄິວ
              <Badge
                variant={tab === "queue" ? "secondary" : "outline"}
                className="h-5 min-w-5 px-1.5 text-[10px] tabular-nums"
              >
                {queueStats.total.toLocaleString("lo-LA")}
              </Badge>
            </Button>
          </div>

          {tab === "logs" ? (
            <QueryState
              result={logsQuery}
              title="ກຳລັງໂຫຼດ"
              description="ດຶງປະຫວັດການສົ່ງ"
            >
              <Card className="gap-0">
                <CardHeader className="space-y-1 pb-3">
                  <CardTitle className="flex items-center gap-2 text-base">
                    <HistoryIcon className="size-4" />
                    ປະຫວັດການສົ່ງ
                  </CardTitle>
                  <CardDescription>
                    ຂໍ້ຄວາມທີ່ສົ່ງ ລົ້ມເຫຼວ ຫຼື ຖືກຂ້າມຜ່ານ Worker
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 pt-0">
                  <NotificationRecordsFilterBar
                    mode="logs"
                    status={logsStatus}
                    onStatusChange={handleLogsStatusChange}
                    filteredTotal={logsQuery.data?.meta.total ?? 0}
                  />
                  <NotificationLogsTable
                    items={logsQuery.data?.items ?? []}
                    hasActiveFilter={logsStatus !== "all"}
                  />
                  <OffsetPaginationBar
                    offset={logsOffset}
                    limit={limit}
                    total={logsQuery.data?.meta.total ?? 0}
                    onChange={setLogsOffset}
                  />
                </CardContent>
              </Card>
            </QueryState>
          ) : (
            <QueryState
              result={queueQuery}
              title="ກຳລັງໂຫຼດ"
              description="ດຶງຄິວ"
            >
              <Card className="gap-0">
                <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0 pb-3">
                  <div className="space-y-1">
                    <CardTitle className="flex items-center gap-2 text-base">
                      <InboxIcon className="size-4" />
                      ຄິວສົ່ງ WhatsApp
                    </CardTitle>
                    <CardDescription>
                      ລາຍການ enqueue — ລໍຖ້າ ກຳລັງສົ່ງ ຫຼື ລົ້ມເຫຼວ
                    </CardDescription>
                  </div>
                  {queueStats.pending > 0 ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      disabled={processQueue.isPending}
                      onClick={() => void handleProcessPendingQueue()}
                    >
                      <PlayIcon className="mr-1 size-4" />
                      ສົ່ງຄິວລໍຖ້າ ({queueStats.pending})
                    </Button>
                  ) : null}
                </CardHeader>
                <CardContent className="space-y-4 pt-0">
                  <NotificationRecordsFilterBar
                    mode="queue"
                    status={queueStatus}
                    onStatusChange={handleQueueStatusChange}
                    filteredTotal={queueQuery.data?.meta.total ?? 0}
                  />
                  <NotificationQueueTable
                    items={queueQuery.data?.items ?? []}
                    hasActiveFilter={queueStatus !== "all"}
                  />
                  <OffsetPaginationBar
                    offset={queueOffset}
                    limit={limit}
                    total={queueQuery.data?.meta.total ?? 0}
                    onChange={setQueueOffset}
                  />
                </CardContent>
              </Card>
            </QueryState>
          )}
        </div>
      </Main>
    </>
  );
}

export default PackageNotificationsLogsPage;
